/**
 * 報酬表ビューア モーダル (v3.2.29+ / v3.3.0 で加算方式に反転)。
 *
 * 報酬設定画面（CommissionSetCategoriesPage のカテゴリーカード / CommissionDetailPage のヘッダー）から起動。
 * セット × カテゴリーを固定して、3 ロール (紹介者 / SA / 社員) の報酬表をタブで表示。
 * SA タブのみ「ベース / 開拓顧客 / 並列」モード切替 + PDF 出力対応。
 * v3.3.0+: PDF 出力カラムの選択チップを追加。
 */
import type { CommissionType, ProductCategory } from '@goenhub/shared';
import {
  COMMISSION_LIST_LABELS,
  COMMISSION_LIST_TYPES,
  resolveSelfDevelopedAdditionRate,
} from '@goenhub/shared';
import type { CommissionListType } from '@goenhub/shared';
import { FileDown } from 'lucide-react';
import { useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { useCommissionSetDetail } from '@/hooks/useCommissions';
import { useCatalogItemsForCommissionSet } from '@/hooks/useProductCatalogs';

import { filterItemsForCategory } from './categoryCatalogLink';
import {
  buildPrintableHtml,
  CommissionTableBody,
  computeRowsCalc,
  defaultPdfColumnSelection,
  openPrintWindow,
  PdfColumnPicker,
  pdfColumnsForMode,
  REFERRAL_MODE_LABEL,
  type PdfColumnSelection,
  type ReferralViewMode,
} from './CommissionTableView';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  setId: string;
  category: Pick<ProductCategory, 'name' | 'catalogIds'> & { id: string };
}

export function CommissionTableViewerDialog({
  open,
  onOpenChange,
  setId,
  category,
}: Props) {
  const detail = useCommissionSetDetail(open ? setId : null);
  // v3.2.30+: 季節モードではなく、このセットに紐付くカタログの価格を参照する
  const allItems = useCatalogItemsForCommissionSet(open ? setId : null);

  const [activeRole, setActiveRole] = useState<CommissionListType>('referrer');
  const [referralViewMode, setReferralViewMode] = useState<ReferralViewMode>('normal');
  // v3.3.0+: PDF 出力カラム選択
  const [pdfColumns, setPdfColumns] = useState<PdfColumnSelection>(
    defaultPdfColumnSelection,
  );

  // カテゴリー対象の productId 集合
  const categoryProductIds = useMemo(() => {
    const matched = filterItemsForCategory(allItems.data ?? [], category);
    return new Set(matched.map((it) => it.id));
  }, [allItems.data, category]);

  // 開拓顧客加算率 (カテゴリー単位、SA タブのみ)。旧「減額率」データは加算率として読み替え。
  const additionRate = useMemo(() => {
    if (!detail.data) return 0;
    return resolveSelfDevelopedAdditionRate(detail.data.set, category.id);
  }, [detail.data, category.id]);

  // 該当ロールの list から、カテゴリー内の items を抜き出す (value > 0 のみ)
  const listsByRole = detail.data?.lists ?? null;
  const filtered = useMemo(() => {
    if (!listsByRole) return null;
    const result: Record<
      CommissionListType,
      { defaultType: CommissionType; items: ReturnType<typeof Array.prototype.slice> }
    > = {
      referrer: { defaultType: 'rate', items: [] },
      sa: { defaultType: 'rate', items: [] },
      employee: { defaultType: 'rate', items: [] },
    };
    for (const lt of COMMISSION_LIST_TYPES) {
      const list = listsByRole[lt];
      if (!list) continue;
      result[lt] = {
        defaultType: list.defaultType,
        items: (list.items ?? []).filter(
          (it) => categoryProductIds.has(it.productId) && it.value > 0,
        ),
      };
    }
    return result;
  }, [listsByRole, categoryProductIds]);

  const currentList = filtered?.[activeRole];

  const rowsCalc = useMemo(() => {
    if (!currentList) return [];
    return computeRowsCalc({
      items: currentList.items,
      allItems: allItems.data ?? [],
      defaultType: currentList.defaultType,
      isSaRole: activeRole === 'sa',
      getAdditionRateForProduct: () => additionRate,
    });
  }, [currentList, allItems.data, activeRole, additionRate]);

  const modeForRender: ReferralViewMode =
    activeRole === 'sa' ? referralViewMode : 'normal';

  const setName = detail.data?.set.name ?? '';

  const handleExportPdf = () => {
    if (!currentList || rowsCalc.length === 0) return;
    const extraNotes: string[] = [];
    if (activeRole === 'sa' && additionRate > 0 && referralViewMode !== 'normal') {
      extraNotes.push(`開拓顧客加算率: +${additionRate}%`);
    }
    const html = buildPrintableHtml({
      title: `報酬表 ${setName} / ${category.name} / ${COMMISSION_LIST_LABELS[activeRole]} (${REFERRAL_MODE_LABEL[modeForRender]})`,
      metaLines: [
        `セット: ${setName}`,
        `カテゴリー: ${category.name}`,
        `対象: ${COMMISSION_LIST_LABELS[activeRole]}`,
        `表示モード: ${REFERRAL_MODE_LABEL[modeForRender]}`,
      ],
      mode: modeForRender,
      rows: rowsCalc,
      extraNotes,
      columns: pdfColumnsForMode(modeForRender).filter((k) => pdfColumns[k]),
    });
    openPrintWindow(html);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            報酬表 — {setName} / {category.name}
          </DialogTitle>
        </DialogHeader>

        {detail.isLoading || allItems.isLoading ? (
          <div className="py-8 text-center">
            <Spinner />
          </div>
        ) : !detail.data ? (
          <p className="py-6 text-sm text-[var(--text-muted)] text-center">
            報酬セットが見つかりません。
          </p>
        ) : (
          <div className="space-y-4">
            {/* ロールタブ */}
            <div className="flex items-center gap-1 border-b border-[var(--border)]">
              {COMMISSION_LIST_TYPES.map((lt) => {
                const count = filtered?.[lt]?.items.length ?? 0;
                return (
                  <button
                    key={lt}
                    type="button"
                    onClick={() => setActiveRole(lt)}
                    className={`px-4 py-2 text-sm border-b-2 transition-colors ${
                      activeRole === lt
                        ? 'border-[var(--accent)] text-[var(--accent)] font-medium'
                        : 'border-transparent text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
                    }`}
                  >
                    {COMMISSION_LIST_LABELS[lt]}{' '}
                    <span className="text-[10px] text-[var(--text-muted)]">
                      ({count})
                    </span>
                  </button>
                );
              })}
            </div>

            {/* SA タブのみモード切替 + 全タブで PDF 出力 */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              {activeRole === 'sa' ? (
                <div className="flex items-center gap-2 rounded-md border border-emerald-300/40 bg-emerald-50/30 dark:bg-emerald-950/10 p-2">
                  <span className="text-[11px] font-medium text-[var(--text-secondary)]">
                    表示モード:
                  </span>
                  {(
                    [
                      { value: 'normal', label: 'ベース' },
                      { value: 'selfDeveloped', label: '開拓顧客' },
                      { value: 'parallel', label: '並列' },
                    ] as const
                  ).map((m) => (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => setReferralViewMode(m.value)}
                      className={`px-2.5 py-1 text-[11px] rounded-md border transition-colors ${
                        referralViewMode === m.value
                          ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                          : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
                      }`}
                    >
                      {m.label}
                    </button>
                  ))}
                  {additionRate > 0 ? (
                    <span className="text-[10px] text-[var(--text-muted)] ml-1">
                      開拓顧客は加算率 +{additionRate}% 適用 (弊社紹介はベースのまま)
                    </span>
                  ) : (
                    <span className="text-[10px] text-[var(--text-muted)] ml-1">
                      加算率未設定 (本カテゴリーで「開拓顧客」モードを使うには SA タブで率を設定)
                    </span>
                  )}
                </div>
              ) : (
                <div className="text-[11px] text-[var(--text-muted)]">
                  {COMMISSION_LIST_LABELS[activeRole]} 報酬表 (開拓顧客加算の対象外)
                </div>
              )}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleExportPdf}
                disabled={rowsCalc.length === 0}
                title="現在の表示モード・選択カラムを PDF として出力"
              >
                <FileDown className="size-3.5" />
                PDF 出力
              </Button>
            </div>

            {/* v3.3.0+: PDF 出力カラム選択 */}
            <PdfColumnPicker
              mode={modeForRender}
              selection={pdfColumns}
              onChange={setPdfColumns}
            />

            {rowsCalc.length === 0 ? (
              <div className="rounded-md border border-[var(--border)] bg-[var(--bg-surface)] p-6 text-center text-xs text-[var(--text-muted)] italic">
                このカテゴリー「{category.name}」では {COMMISSION_LIST_LABELS[activeRole]}{' '}
                報酬が未設定です。
              </div>
            ) : (
              <CommissionTableBody rows={rowsCalc} mode={modeForRender} />
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
